import '@ui5/webcomponents-icons/dist/chain-link';
import {
  Button,
  FlexBox,
  FormItem,
  Input,
  Label,
  Option,
  Select,
  Text
} from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import {
  DocumentType,
  IssueType,
  NotesQueryDetailsFragment,
  useUpdateNotesQueryMutation,
  Worklist
} from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useUpdateNotesQueryStore } from 'stores/UpdateNotesQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateSAPUserId, validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';
import { WorklistOptions } from 'stores/interfaces/CommonQueryState';
import { compareAlphabetically } from 'utils/sorting';
import { env } from 'config/env';
import { openWindow } from 'utils/windowOpen';

import ButtonDesign from '@ui5/webcomponents/dist/types/ButtonDesign.js';
import InputType from '@ui5/webcomponents/dist/types/InputType.js';
import ValueState from '@ui5/webcomponents-base/dist/types/ValueState.js';
import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

const notesUrl = env?.REACT_APP_NOTES_URL as string;

export interface DisplayQueryDetailsNotesProps {
  queryId: string;
  queryDetails: NotesQueryDetailsFragment;
}

export const DisplayQueryDetailsNotes = forwardRef<
  SpecificQueryType,
  DisplayQueryDetailsNotesProps
>((props: DisplayQueryDetailsNotesProps, ref) => {
  const { queryDetails, queryId } = props;
  const { t } = useTranslation();
  const notesQueryStore = useUpdateNotesQueryStore();
  const baseQueryStore = useUpdateBaseQueryStore();
  const { editMode, setCancelEditing } = useEditQueryStore();
  const [, callUpdateQuery] = useUpdateNotesQueryMutation();
  const classes = useStyles();

  useImperativeHandle(ref, () => ({
    cancel: () => {
      notesQueryStore.setInitialStateWithProps(queryDetails);
    },
    save: () => {
      return saveQuery();
    },
    isValid: () => {
      return validateAllFields();
    }
  }));

  useEffect(() => {
    notesQueryStore.setInitialStateWithProps(queryDetails);
  }, []);

  const onChangeDocumentNumber = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    notesQueryStore.setDocumentNumber(value);
    validateString(value, notesQueryStore.setDocumentNumberValueState);
  };

  const onChangeComponent = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.target as HTMLInputElement;
    notesQueryStore.setComponent(value);
    validateString(value, notesQueryStore.setComponentValueState);
  };

  const onChangeWorklist = (event: CustomEvent) => {
    setCancelEditing(false);
    const { value } = event.detail.selectedOption.dataset;
    notesQueryStore.setWorklist(value);
  };

  const onChangeDocumentResponsible = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    notesQueryStore.setDocumentResponsible(value);
    validateDocumentResponsible(value);
  };

  const saveQuery = async () => {
    const result = await callUpdateQuery({
      input: {
        id: Number(queryId),
        sourceText: baseQueryStore.sourceText,
        targetText: baseQueryStore.targetText,
        deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
        targetLanguage: baseQueryStore.targetLanguage,
        issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
        details: baseQueryStore.details,
        ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
        distributions: baseQueryStore.distributions,
        resetedDistributions: baseQueryStore.resetedDistributions,
        documentNumber: notesQueryStore.documentNumber,
        component: notesQueryStore.component,
        documentType: notesQueryStore.documentType as DocumentType,
        worklist:
          notesQueryStore.worklist === WorklistOptions.NOT_SELECTED_WORKLIST
            ? null
            : (notesQueryStore.worklist as Worklist),
        qeReview: baseQueryStore.qeReview,
        documentResponsible: notesQueryStore.documentResponsible?.toUpperCase()
      }
    });
    return { error: result.error };
  };

  const validateDocumentNumber = () => {
    return validateString(
      notesQueryStore.documentNumber,
      notesQueryStore.setDocumentNumberValueState
    );
  };

  const validateComponent = () => {
    return validateString(notesQueryStore.component, notesQueryStore.setComponentValueState);
  };

  const validateDocumentResponsible = (value = notesQueryStore.documentResponsible) => {
    if (notesQueryStore.documentType === DocumentType.SAP_NOTE) {
      const isNotEmpty = validateString(
        value || '',
        notesQueryStore.setDocumentResponsibleValueState
      );
      if (!isNotEmpty) return false;
    }
    if (value) {
      return validateSAPUserId(value, notesQueryStore.setDocumentResponsibleValueState);
    } else {
      notesQueryStore.setDocumentResponsibleValueState(ValueState.None);
    }
    return true;
  };

  const validateAllFields = () => {
    const isDocumentNumberValid = validateDocumentNumber();
    const isComponentValid = validateComponent();
    const isDocumentResponsibleValid = validateDocumentResponsible();
    return isDocumentNumberValid && isComponentValid && isDocumentResponsibleValid;
  };

  const onOpenNote = () => {
    openWindow(`${notesUrl}${notesQueryStore.documentNumber}`);
  };

  const getDetailsData = (): FormDisplayItem[] => {
    const itemsToDisplay: FormDisplayItem[] = [
      {
        renderComponent: () => (
          <Input
            data-testid="edit-document-number"
            data-help-id="edit-document-number"
            aria-labelledby={t('document-number-label')}
            type={InputType.Number}
            value={notesQueryStore.documentNumber}
            className={classes.formLineInput}
            onChange={onChangeDocumentNumber}
          />
        ),
        renderNotEditableComponent: (entry: FormDisplayItem) => (
          <FlexBox direction="Row" className={classes.documentNumberInputContainer}>
            <Text
              data-testid={entry.dataTestId}
              aria-labelledby={entry.i18nTag}
              className={classes.formItemText}
            >
              {entry.data}
            </Text>
            <Button
              data-testid="display-query-details-notes-open-note"
              data-help-id="display-query-details-notes-open-note"
              design={ButtonDesign.Emphasized}
              tooltip={t('display-query-details-notes-open-document-tooltip')}
              icon="chain-link"
              onClick={onOpenNote}
            />
          </FlexBox>
        ),
        dataTestId: 'document-number',
        data: notesQueryStore.documentNumber,
        i18nTag: t('document-number-label'),
        required: true
      },
      {
        renderComponent: () => (
          <Input
            value={notesQueryStore.component}
            aria-labelledby={t('component-label')}
            data-testid="edit-component"
            data-help-id="edit-component"
            className={classes.formLineInput}
            valueState={notesQueryStore.componentValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-application-component')}
              </Label>
            }
            onChange={onChangeComponent}
          />
        ),
        dataTestId: 'component',
        data: notesQueryStore.component,
        i18nTag: t('component-label'),
        required: true
      },
      {
        renderNotEditableComponent: (entry: FormDisplayItem) => (
          <Text
            data-testid={entry.dataTestId}
            aria-labelledby={entry.i18nTag}
            className={classes.formItemText}
          >
            {entry.data}
          </Text>
        ),
        dataTestId: 'document-type',
        data: t(notesQueryStore.documentType),
        i18nTag: t('document-type-label'),
        editable: false
      },
      {
        renderComponent: () => (
          <Input
            value={notesQueryStore.documentResponsible || ''}
            aria-labelledby={t('document-responsible-label')}
            data-testid="edit-document-responsible"
            data-help-id="edit-document-responsible"
            className={classes.formLineInput}
            valueState={notesQueryStore.documentResponsibleValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-document-responsible')}
              </Label>
            }
            onChange={onChangeDocumentResponsible}
          />
        ),
        dataTestId: 'document-responsible',
        data: queryDetails.documentResponsibleDisplayName,
        i18nTag: t('document-responsible-label'),
        required: notesQueryStore.documentType === DocumentType.SAP_NOTE
      }
    ];
    if (notesQueryStore.documentType === DocumentType.SAP_NOTE) {
      itemsToDisplay.push({
        renderComponent: () => (
          <Select
            aria-labelledby={t('worklist-label')}
            data-testid="edit-worklist"
            data-help-id="edit-worklist"
            className={classes.formLineInput}
            onChange={onChangeWorklist}
            valueState={notesQueryStore.worklistValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-worklist')}
              </Label>
            }
            title={t(notesQueryStore.worklist)}
          >
            {Object.values(WorklistOptions)
              .sort((a, b) => compareAlphabetically(t(a), t(b)))
              .map((worklist) => (
                <Option
                  key={worklist}
                  data-value={worklist}
                  selected={worklist === notesQueryStore.worklist}
                >
                  {t(worklist)}
                </Option>
              ))}
          </Select>
        ),
        dataTestId: 'worklist',
        data:
          notesQueryStore.worklist !== WorklistOptions.NOT_SELECTED_WORKLIST
            ? t(notesQueryStore.worklist)
            : null,
        i18nTag: t('worklist-label')
      });
    }
    return itemsToDisplay;
  };

  const renderDetailItem = (entry: FormDisplayItem) => {
    if (editMode && entry.editable !== false) {
      return entry.renderComponent();
    }

    if (!editMode && entry.renderNotEditableComponent) {
      return entry.renderNotEditableComponent(entry);
    }

    return (
      <Text
        data-testid={entry.dataTestId}
        aria-labelledby={entry.i18nTag}
        className={classes.formItemText}
      >
        {entry.data}
      </Text>
    );
  };

  let queryDetailsData: FormDisplayItem[] = getDetailsData();

  useEffect(() => {
    queryDetailsData = getDetailsData();
  }, [queryDetails]);

  return (
    <>
      {queryDetailsData.map((entry) => (
        <FormItem
          key={entry.dataTestId}
          labelContent={
            <Label
              id={entry.i18nTag}
              required={editMode && entry.required}
              data-help-id={entry.dataTestId}
              className={classes.formItemLabel}
              wrappingType="None"
            >
              {entry.i18nTag}
            </Label>
          }
        >
          {renderDetailItem(entry)}
        </FormItem>
      ))}
    </>
  );
});
